import React, { useEffect, useState } from "react";
import { Badge, Button, Spinner } from "react-bootstrap";
import { useAuthUser } from "../../features/auth/authHooks";
import { apiService } from "../../common/api/apiService";
import { API_ENDPOINTS } from "../../common/api/apiEndpoints";

interface PersonIdentityData {
  hasPersonalIdentityCode: boolean;
  firstName?: string;
  lastName?: string;
  issuedAt?: string;
  personalIdentityCode?: string;
  updatedAt?: string;
}

const PersonalIdentityInfo: React.FC = () => {
  const { ownerAccountId, isLoggedIn } = useAuthUser();
  const [loading, setLoading] = useState(true);
  const [personalIdentityData, setPersonalIdentityData] =
    useState<PersonIdentityData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPersonalIdentityData = async () => {
      if (!isLoggedIn || !ownerAccountId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data: PersonIdentityData = await apiService.get(
          `${API_ENDPOINTS.IDENTITY.GET_PERSON_IDENTITY_DATA}?accountId=${ownerAccountId}`,
        );
        setPersonalIdentityData(data);
      } catch (e: any) {
        console.error("Error fetching personal identity data:", e);
        setError("Vahvan tunnistautumisen tietojen haku epäonnistui.");
      } finally {
        setLoading(false);
      }
    };

    fetchPersonalIdentityData();
  }, [isLoggedIn, ownerAccountId]);

  const handleStrongAuth = () => {
    const returnUrl = encodeURIComponent(
      "/Account/IdentityVerified?omaliittyma=true",
    );
    window.location.href = `/local-api/StrongAuth/challenge?returnUrl=${returnUrl}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-24">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Ladataan...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-24">
        <h2 className="h5 mb-16">Vahva tunnistautuminen</h2>
        <p className="text-danger">{error}</p>
      </div>
    );
  }

  let statusBadge;
  if (personalIdentityData?.hasPersonalIdentityCode) {
    statusBadge = <Badge bg="success">Tallennettu</Badge>;
  } else {
    statusBadge = (
      <Badge bg="danger">
        <span className="bi bi-exclamation-triangle me-5 fs-6"></span> Puuttuu
      </Badge>
    );
  }

  return (
    <div className="mb-24">
      <h2 className="h5 mb-16">Vahva tunnistautuminen</h2>

      <div className="d-flex align-items-center mb-8">
        <span className="me-12">Tila:</span>
        {statusBadge}
      </div>

      {personalIdentityData?.hasPersonalIdentityCode ? (
        <>
          {personalIdentityData.issuedAt && (
            <div className="text-muted mb-12">
              Vahva tunnistautuminen tehty:{" "}
              {new Date(personalIdentityData.issuedAt).toLocaleString("fi-FI")}
            </div>
          )}
          {personalIdentityData.updatedAt && (
            <div className="text-muted mb-12">
              Tietoja muokattu:{" "}
              {new Date(personalIdentityData.updatedAt).toLocaleString("fi-FI")}
            </div>
          )}
        </>
      ) : (
        <div className="mt-16">
          <p className="mb-12">
            Palvelun käyttö edellyttää vahvaa tunnistautumista pankkitunnuksilla
            tai mobiilivarmenteella.
          </p>
          <Button variant="primary" onClick={handleStrongAuth}>
            <span className="bi bi-shield-check me-8"></span>
            Tunnistaudu
          </Button>
        </div>
      )}
    </div>
  );
};

export default PersonalIdentityInfo;
